import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScoreBar, NumberInput, VASSlider } from "./shared";
import { JointSelectorPsA } from "./joint-selector-psa";

export function DAPSACalc() {
  const [useMap, setUseMap] = useState(false);
  const [tjc, setTjc]       = useState(0);
  const [sjc, setSjc]       = useState(0);
  const [pain, setPain]     = useState(0);
  const [global, setGlobal] = useState(0);
  const [crp, setCrp]       = useState(0);

  const dapsa = tjc + sjc + pain + global + crp;

  const bands = [
    { label: "Remission", max: 4,   colour: "text-emerald-700" },
    { label: "Low",       max: 14,  colour: "text-teal-600" },
    { label: "Moderate",  max: 28,  colour: "text-amber-600" },
    { label: "High",      max: 200, colour: "text-red-600" },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">DAPSA</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Badge variant="outline">PsA</Badge>
            <Badge variant="outline">EULAR / GRAPPA</Badge>
            <Badge variant="outline">TJC68 / SJC66</Badge>
          </div>
        </div>
        <p className="text-xs text-slate-500 font-mono">TJC68 + SJC66 + Pain VAS(0–10) + Global VAS(0–10) + CRP(mg/dL)</p>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Joint map toggle */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setUseMap(!useMap)}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${useMap ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"}`}
          >
            {useMap ? "✓ Using Joint Map (60 joints)" : "Use Visual Joint Map"}
          </button>
          {useMap && (
            <div className="text-xs text-slate-500 bg-slate-100 rounded-lg px-3 py-1.5">
              TJC: <strong className="text-orange-600">{tjc}</strong> · SJC: <strong className="text-blue-600">{sjc}</strong>
            </div>
          )}
        </div>

        {useMap ? (
          <JointSelectorPsA
            label="DAPSA — 60-Joint Map (TJC68/SJC66 subset)"
            onChange={(t, s) => { setTjc(t); setSjc(s); }}
          />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            <NumberInput label="Tender Joint Count (TJC68)" value={tjc} onChange={setTjc} min={0} max={68} hint="All peripheral joints" />
            <NumberInput label="Swollen Joint Count (SJC66)" value={sjc} onChange={setSjc} min={0} max={66} hint="Excludes hips" />
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <VASSlider label="Patient pain VAS (0–10)" value={pain} onChange={setPain} />
          <VASSlider label="Patient global assessment (0–10)" value={global} onChange={setGlobal} />
          <NumberInput
            label="CRP (mg/dL)"
            value={crp}
            onChange={setCrp}
            min={0}
            step={0.01}
            hint="Note: mg/dL (divide mg/L by 10)"
          />
        </div>

        <ScoreBar score={dapsa} bands={bands} />
        <p className="text-xs text-slate-500">Remission ≤4 · Low ≤14 · Moderate ≤28 · High &gt;28</p>
        <p className="text-xs text-slate-400">
          DAPSA-CRP — validated treat-to-target measure for PsA (EULAR 2023).
          Joint map covers 60 major peripheral joints (subset of TJC68/SJC66).
        </p>
      </CardContent>
    </Card>
  );
}
